/* eslint-disable react/no-unescaped-entities */

/* eslint-disable no-param-reassign */
import { Flex, Spinner } from "@engaging-tech/components";
import React, { useCallback, useEffect, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"

import PrimaryButton from "../../ui/components/PrimaryButton"
import flattenFilters from "../../utils/flattenFilters"
import DisplayTable from "../components/DisplayTable"
import { config, headings, mobileConfig, mobileHeadings } from "../lib/companyTableConfig"
import { loadCompanies, loadMoreCompanies } from "../store/rankings.actions"
import {
  getActiveFilters,
  getCompanies,
  getCompaniesIsLoading,
  getCompaniesRankingOffset,
  getCompaniesRankingTotal,
  getMoreCompaniesIsLoading,
  getSearchTerm
} from "../store/rankings.selectors"
import { LoadMoreWrapper } from "../styles"

const types = {
  country: "country",
  industry: "industry",
  category: "category",
  year: "year"
}

const updateField = (appliedFields, filter, type) => {
  const fieldIndex = appliedFields.findIndex(field => field.type === type)
  const typeObj = { type, ...filter }
  if (fieldIndex === -1) {
    appliedFields.push(typeObj)
  } else {
    appliedFields[fieldIndex] = typeObj
  }
}

const generateSelectedFields = (
  selectedFields,
  countryFilter,
  industryFilter,
  categoryFilter,
  yearFilter
) => {
  let appliedFields = [...selectedFields.current]

  if (countryFilter) updateField(appliedFields, countryFilter, types.country)
  if (industryFilter) updateField(appliedFields, industryFilter, types.industry)
  if (categoryFilter) updateField(appliedFields, categoryFilter, types.category)
  if (yearFilter) updateField(appliedFields, yearFilter, types.year)

  if (countryFilter === undefined) appliedFields = appliedFields.filter(field => field.type !== types.country)
  if (industryFilter === undefined)
    appliedFields = appliedFields.filter(field => field.type !== types.industry)
  if (categoryFilter === undefined)
    appliedFields = appliedFields.filter(field => field.type !== types.category)
  if (yearFilter === undefined) appliedFields = appliedFields.filter(field => field.type !== types.year)
  selectedFields.current = [...appliedFields]
}

export default function HappiestCompaniesContent() {
  const appliedFilters = useSelector(getActiveFilters)
  const companiesList = useSelector(getCompanies)
  const isLoading = useSelector(getCompaniesIsLoading)
  const isLoadingMore = useSelector(getMoreCompaniesIsLoading)
  const offset = useSelector(getCompaniesRankingOffset)
  const total = useSelector(getCompaniesRankingTotal)
  const searchTerm = useSelector(getSearchTerm)

  const hasMorePages = offset < total

  const selectedFields = useRef([])

  const dispatch = useDispatch()

  useEffect(() => {
    // eslint-disable-next-line no-use-before-define
    onFilterChange()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    searchTerm,
    appliedFilters.industry,
    appliedFilters.country,
    appliedFilters.award,
    appliedFilters.demographicFilters,
    appliedFilters.year
  ])

  const isDemographicFiltersSelected =
    Object.values(appliedFilters.demographicFilters)?.length &&
    flattenFilters(Object.values(appliedFilters.demographicFilters))?.length

  const onFilterChange = useCallback(() => {
    dispatch(
      loadCompanies({
        searchTerm,
        industryFilter: appliedFilters.industry,
        countryFilter: appliedFilters.country,
        categoryFilter: appliedFilters.award,
        yearFilter: appliedFilters.year,
        filters: appliedFilters.demographicFilters
      })
    )

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    searchTerm,
    appliedFilters.industry,
    appliedFilters.country,
    appliedFilters.award,
    appliedFilters.demographicFilters,
    appliedFilters.year
  ])

  const onLoadMore = () => {
    dispatch(
      loadMoreCompanies({
        searchTerm,
        industryFilter: appliedFilters.industry,
        countryFilter: appliedFilters.country,
        categoryFilter: appliedFilters.award,
        yearFilter: appliedFilters.year,
        filters: appliedFilters.demographicFilters
      })
    )
  }

  generateSelectedFields(
    selectedFields,
    appliedFilters.country,
    appliedFilters.industry,
    appliedFilters.award,
    appliedFilters.year
  )

  return (
    <Flex flexDirection="column" justifyContent="center">
      <DisplayTable
        config={config}
        rows={companiesList?.length ? companiesList : []}
        headings={headings}
        mobileConfig={mobileConfig}
        mobileHeadings={mobileHeadings}
        isLoading={isLoading}
        isDemographicFiltersSelected={isDemographicFiltersSelected}
        type="companies"
        width={100}
      />

      <LoadMoreWrapper>
        {isLoadingMore ? (
          <Spinner mt={4} mb={4} color="primary.0" width={1} />
        ) : (
          <PrimaryButton
            onClick={onLoadMore}
            width={1 / 1}
            maxWidth={300}
            disabled={!hasMorePages || isLoading || isLoadingMore}
            color="light.0"
            text={!hasMorePages ? "Loaded all companies" : "Load more"}
          />
        )}
      </LoadMoreWrapper>
    </Flex>
  )
}
